/* ONE EMAIL, TWO FORMS (her call, 2026-10-12): PLAIN — the words, for
   anywhere — and DESIGNED — her logo, a header picture, headings, a readable
   width and the ask as a button, in HER brand colours. Where it is going
   decides which: pasted into Gmail it must arrive formatted, into Mailchimp or
   Kit it can arrive as code, into GHL as a template. The same two strings
   serve all three, so they can never disagree about what the email says.

   Knows nothing about a newsletter or a sequence: it is handed the parts
   (`emailParts` below reads a newsletter; Outreach hands a touch's subject and
   body). The SUBJECT is never in the body — it goes in the subject field, and
   GHL's templates cannot even hold one — so it is returned beside, not inside.

   Email HTML is its own dialect: tables, inline styles, no <style> a client
   can strip, no web fonts (Gmail drops them), images with width and alt. */

import { blockKindDef } from "./newsletters";
import { contrast } from "./deckLayout";

/* ── The parts ────────────────────────────────────────────────────────────── */

/** A newsletter email's parts, read from its blocks in her order. */
export function emailParts(newsletter, blocks = []) {
  const parts = { subject: "", preview: "", sections: [], ask: "", fullVersion: String(newsletter?.canonicalUrl || "").trim() };
  for (const b of blocks || []) {
    const c = b?.content || {};
    if (b.blockType === "subject") {
      parts.subject = String(c.subject || "").trim();
      parts.preview = String(c.preview || "").trim();
      continue;
    }
    const text = String(c.text || "").trim();
    if (b.blockType === "ask") { if (text) parts.ask = text; continue; }
    const heading = blockKindDef(b.blockType).fields.some((f) => f.key === "heading") ? String(c.heading || "").trim() : "";
    if (text || heading) parts.sections.push({ heading, text });
  }
  return parts;
}

/* ── Hi, and Talk soon ────────────────────────────────────────────────────── */

/* HER ASK, 2026-11-02: an email opens on the reader's first name and closes on
   hers. GHL fills `{{contact.first_name}}` per contact; anywhere else a
   visible [first name] says "your tool's name goes here" rather than guessing
   at Mailchimp's or Kit's token.

   BRILL OFTEN WRITES BOTH ALREADY — four of six Outreach emails opened
   "Hi [name]," and three closed "Talk soon, / [Her name]" (2026-11-03,
   scripts/check-email-signoff.js). So this FILLS what is there and adds only
   what is missing; it never says hello twice. Her own words win: a greeting
   or a sign-off she wrote stays as she wrote it, only the blanks are filled. */
export const GHL_FIRST_NAME = "{{contact.first_name}}";
const THEIR_SLOT = /\[(?:their |client'?s? |contact'?s? |first |recipient'?s? )?name\]/gi;
const HER_SLOT = /\[(?:her|your|my|coach'?s?) (?:first )?name\]/gi;
const GREETING_WORD = /^(hi|hey|hello|dear)\b/i;
const GREETING_LINE = /^(hi|hey|hello|dear)\b[^\n]{0,40}$/i;
const SIGNOFF_LINE = /^(talk soon|speak soon|warmly|warm regards|with warmth|best|all the best|cheers|thanks|thank you|with love|love|xo|regards|kind regards|until next time|see you soon)(,[^\n]{0,30}|!|\.)?$/i;

/**
 * The parts with `greeting` and `signOff` set and the blanks filled.
 * `to` is the reader's name token ("" → "[first name]"); `signer` is her name
 * ("" → "[your name]"). Returns new parts; never changes the ones handed in.
 */
export function personaliseEmail(parts, { to = "", signer = "" } = {}) {
  const them = to || "[first name]";
  const me = String(signer || "").trim() || "[your name]";
  const fill = (s) => String(s || "").replace(THEIR_SLOT, them).replace(HER_SLOT, me);
  const sections = (parts?.sections || []).map((s) => ({ heading: fill(s.heading), text: fill(s.text) }));
  const out = { ...parts, sections, ask: fill(parts?.ask) };

  /* THE GREETING: a short first line that is one is lifted out and kept; a
     first sentence that merely STARTS with "Hi!" is hers and left where it
     is, with nothing added above it. */
  const first = sections[0];
  const lines = first && !first.heading ? first.text.trim().split("\n") : [];
  if (lines.length && GREETING_LINE.test(lines[0].trim())) {
    out.greeting = lines[0].trim();
    first.text = lines.slice(1).join("\n").trim();
  } else if (lines.length && GREETING_WORD.test(lines[0].trim())) {
    out.greeting = "";
  } else {
    out.greeting = `Hi ${them},`;
  }

  /* THE SIGN-OFF: looked for at the very end, where the ask is not. */
  const lastKey = out.ask ? "ask" : null;
  const last = lastKey ? null : sections[sections.length - 1];
  const tail = String(lastKey ? out.ask : last?.text || "").trim().split("\n");
  let word = "", name = "", cut = 0;
  const at = (i) => (tail[i] || "").trim();
  if (tail.length >= 2 && SIGNOFF_LINE.test(at(tail.length - 2)) && at(tail.length - 1).length <= 40 && !SIGNOFF_LINE.test(at(tail.length - 1))) {
    word = at(tail.length - 2); name = at(tail.length - 1); cut = 2;
  } else if (tail.length && SIGNOFF_LINE.test(at(tail.length - 1))) {
    word = at(tail.length - 1); cut = 1;
  }
  if (cut) {
    const kept = tail.slice(0, tail.length - cut).join("\n").trim();
    if (lastKey) out.ask = kept; else last.text = kept;
    /* "Talk soon, Tevra" on one line already carries a name. */
    const oneLine = cut === 1 && /,\s*\S/.test(word);
    out.signOff = oneLine ? word : `${word.replace(/[,.!\s]*$/, ",")}\n${name || me}`;
  } else {
    out.signOff = `Talk soon,\n${me}`;
  }
  out.sections = sections.filter((s) => s.heading || s.text);
  return out;
}

/* ── Plain ────────────────────────────────────────────────────────────────── */

/** The email's body as words. Headings stay as their own line. */
export function emailPlain(parts) {
  const out = [];
  if (parts?.greeting) out.push(parts.greeting);
  for (const s of parts?.sections || []) {
    if (s.heading) out.push(s.heading);
    if (s.text) out.push(s.text);
  }
  if (parts?.ask) out.push(parts.ask);
  if (parts?.signOff) out.push(parts.signOff);
  if (parts?.fullVersion) out.push(`The full version: ${parts.fullVersion}`);
  return out.join("\n\n");
}

/* ── Designed ─────────────────────────────────────────────────────────────── */

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const URL_RE = /\bhttps?:\/\/[^\s<>"')]+[^\s<>"').,!?;:]/g;
const hex = (v, fallback) => {
  const h = String(v || "").replace(/^#/, "").trim();
  return /^[0-9a-f]{6}$/i.test(h) ? `#${h}` : fallback;
};

/* A link in her words stays a link. Escaped first, so nothing she typed is
   ever read as markup. */
function linkify(text, color) {
  return esc(text).replace(URL_RE, (u) => `<a href="${u}" style="color:${color};text-decoration:underline;">${u}</a>`);
}

/* Paragraphs on a blank line, line breaks within one. */
function paragraphs(text, style, linkColor) {
  return String(text || "").trim().split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
    .map((p) => `<p style="${style}">${linkify(p, linkColor).replace(/\n/g, "<br>")}</p>`).join("");
}

/** The colours the design wears: her locked palette where it was readable
 *  (`readBrandSkin`), a quiet neutral where it was not — never a guess at
 *  someone else's brand. */
export function emailColors(skin = {}) {
  const text = readable(hex(skin.textOnLight, ""), 4.5) || "#2C2C2C";
  /* EVERY COLOUR ON THE WHITE CARD HAS TO READ ON WHITE (2026-10-12): her
     accent is Electric Teal, which her own brand rules keep to dark
     backgrounds — as a button with white words it was 1.5:1. So a colour is
     used only where it passes (4.5:1 for words and the button's white text,
     3:1 for a large heading); otherwise the next brand colour, then her text
     colour. Never a colour that cannot be read. */
  const brand = readable(hex(skin.brandPrimary, ""), 3);
  /* HER BUTTON WITH HER WORDS ON IT (Me → Brand, 2026-10-12): Electric Teal
     with Deep Navy words passes, so it is used — the white-words rule only
     applies where she has not said what goes on her button. */
  const ownBtn = hex(skin.brandAccent, ""), ownText = hex(skin.buttonText, "");
  const pairs = ownBtn && ownText && contrast(ownBtn.slice(1), ownText.slice(1)) >= 4.5;
  return {
    page: hex(skin.bgPrimary, "#F4F3F0"),
    card: "#FFFFFF",
    text,
    heading: brand || text,
    link: readable(hex(skin.link, ""), 4.5) || readable(hex(skin.brandPrimary, ""), 4.5) || text,
    button: pairs ? ownBtn : readable(ownBtn, 4.5) || readable(hex(skin.brandPrimary, ""), 4.5) || text,
    buttonText: pairs ? ownText : "#FFFFFF",
    muted: "#6B6B6B",
  };
}

/* The colour, if it reads against white at `ratio` (WCAG); else "". */
function readable(color, ratio) {
  return color && contrast(color.slice(1), "FFFFFF") >= ratio ? color : "";
}

/**
 * The designed email as one HTML document. `brand` is { logoUrl, name, skin }
 * (the name is the logo's alt — many inboxes hide pictures until asked);
 * `picture` is { url, alt } for the header. The ask becomes a BUTTON only when
 * it carries a link to press — a button that goes nowhere is worse than words.
 */
export function emailHtml(parts, { brand = {}, picture = null } = {}) {
  const c = emailColors(brand.skin);
  const font = "Helvetica, Arial, sans-serif";
  const body = `margin:0 0 16px;font-family:${font};font-size:16px;line-height:1.6;color:${c.text};`;
  const rows = [];

  if (brand.logoUrl) {
    rows.push(`<tr><td style="padding:28px 32px 8px;text-align:center;"><img src="${esc(brand.logoUrl)}" alt="${esc(brand.name || "")}" height="48" style="height:48px;width:auto;max-width:240px;border:0;display:inline-block;"></td></tr>`);
  }
  if (picture?.url) {
    rows.push(`<tr><td style="padding:${brand.logoUrl ? "16px" : "0"} 0 0;"><img src="${esc(picture.url)}" alt="${esc(picture.alt || "")}" width="600" style="width:100%;max-width:600px;height:auto;border:0;display:block;"></td></tr>`);
  }

  const inner = [];
  if (parts?.greeting) inner.push(paragraphs(parts.greeting, body, c.link));
  for (const s of parts?.sections || []) {
    if (s.heading) inner.push(`<h2 style="margin:8px 0 12px;font-family:${font};font-size:21px;line-height:1.3;font-weight:700;color:${c.heading};">${esc(s.heading)}</h2>`);
    if (s.text) inner.push(paragraphs(s.text, body, c.link));
  }
  if (parts?.ask) {
    const link = String(parts.ask).match(URL_RE)?.[0] || "";
    if (link) {
      const words = String(parts.ask).replace(link, "").replace(/\s+([.,!?;:])/g, "$1").replace(/[:\s–—-]+$/, "").trim();
      /* Her last short clause BECOMES the button, so it is not said twice. */
      const label = buttonLabel(words);
      const sentences = words.split(/(?<=[.!?])\s+/).filter(Boolean);
      const lead = label !== "Find out more" ? sentences.slice(0, -1).join(" ") : words;
      if (lead) inner.push(paragraphs(lead, body, c.link));
      inner.push(`<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 20px;"><tr><td style="border-radius:8px;background:${c.button};"><a href="${link}" style="display:inline-block;padding:13px 26px;font-family:${font};font-size:16px;font-weight:700;color:${c.buttonText};text-decoration:none;border-radius:8px;">${esc(label)}</a></td></tr></table>`);
    } else {
      inner.push(paragraphs(parts.ask, `${body}font-weight:700;`, c.link));
    }
  }
  if (parts?.signOff) inner.push(paragraphs(parts.signOff, body, c.link));
  if (parts?.fullVersion) {
    inner.push(`<p style="margin:8px 0 0;font-family:${font};font-size:14px;line-height:1.6;color:${c.muted};">The full version: <a href="${esc(parts.fullVersion)}" style="color:${c.link};">${esc(parts.fullVersion)}</a></p>`);
  }
  rows.push(`<tr><td style="padding:28px 32px 32px;">${inner.join("")}</td></tr>`);

  /* The PREVIEW line rides as hidden text at the very top — the inbox reads
     the first words of the body, so that is where it has to be. */
  const preheader = parts?.preview
    ? `<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:${c.page};opacity:0;">${esc(parts.preview)}</div>`
    : "";

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${esc(parts?.subject || "")}</title></head>
<body style="margin:0;padding:0;background:${c.page};">
${preheader}<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${c.page};"><tr><td align="center" style="padding:24px 12px;">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;background:${c.card};border-radius:10px;overflow:hidden;">
${rows.join("\n")}
</table></td></tr></table>
</body></html>`;
}

/* The button says what the ask says, short. Her last clause before the link
   ("Book your call here") is usually the right words; past six words it
   becomes a sentence, and a sentence is not a button. */
export function buttonLabel(words) {
  const last = String(words || "").split(/(?<=[.!?])\s+/).filter(Boolean).pop() || "";
  const clean = last.replace(/[.!?:]+$/, "").replace(/\s+here$/i, "").trim();
  return clean && clean.split(/\s+/).length <= 6 ? clean : "Find out more";
}

/* ── The clipboard ────────────────────────────────────────────────────────────
   FORMATTED copy puts BOTH on the clipboard — the design for anything that
   takes formatting (Gmail, Outlook, a Mailchimp text block) and the words for
   anything that does not. Where the browser cannot write HTML, it falls back
   to the words and SAYS so: "nothing happened" is never the outcome. */
export async function copyFormatted(html, plain) {
  try {
    if (typeof window !== "undefined" && window.ClipboardItem && navigator.clipboard?.write) {
      await navigator.clipboard.write([new window.ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plain], { type: "text/plain" }),
      })]);
      return { ok: true, formatted: true };
    }
    await navigator.clipboard.writeText(plain);
    return { ok: true, formatted: false };
  } catch (err) {
    return { ok: false, error: err?.message || "The browser refused the clipboard." };
  }
}

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err?.message || "The browser refused the clipboard." };
  }
}

/* PLAIN or DESIGNED — remembered per browser and per PLACE, the last one she
   used there: a newsletter starts Designed, an outreach email Plain (one
   person writing to another), and choosing one must not flip the other. A
   convenience only: unreadable storage just means the default. */
const formatKey = (where) => `brill.email.format.${where}`;
export function readEmailFormat(where, fallback) {
  try { const v = window.localStorage.getItem(formatKey(where)); return v === "plain" || v === "designed" ? v : fallback; } catch { return fallback; }
}
export function writeEmailFormat(where, v) {
  try { window.localStorage.setItem(formatKey(where), v); } catch { /* a convenience */ }
}
